import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Palette, Settings, Moon, Sun, Printer, Database, Download, Upload, Flag, Clock } from "lucide-react";
import CalculadoraCorte from "./pages/corte/CalculadoraCorte";
import ErrorBoundary from "./components/ErrorBoundary";
import PalpitarPublico from "./components/corte/PalpitarPublico";
import CardapioPublico from "./components/cardapio/CardapioPublico";
import PaginaPublica from "./components/PaginaPublica";
import splashImg from "./assets/splash.png";
import { moduloFixoDaUrl } from "./pages/corte/abasModulo";
import {
  encerrarSessaoDesktop,
  authFilhaViaUrl,
  iniciarHeartbeatSessaoDesktop,
  registrarLoginDesktop,
  SESSAO_DESKTOP_STORAGE_KEY,
} from "./utils/adeSessaoDesktop";
import {
  baixarJsonBackup,
  diagnosticarTodasFontesDados,
  extrairLotesDeBackupJson,
  montarBackupCompleto,
  restaurarBackupCompleto,
} from "./utils/adeBackupCompleto";
import {
  backupAutomaticoEstaAtivo,
  caminhoBackupAutomatico,
  estimarProximoBackupAutomatico,
  executarBackupAutomatico,
  formatarDataBackup,
  iniciarServicoBackupAutomatico,
  INTERVALO_BACKUP_AUTOMATICO_MS,
  lerMetaBackupAutomatico,
  MAX_ROTATIVOS_POR_MODULO,
  pararServicoBackupAutomatico,
  pastaBackupsRaiz,
  pastaModuloBackup,
  setBackupAutomaticoAtivo,
  tentarRestaurarBackupMarcadoUmaVez,
  type MetaBackupAutomatico,
} from "./utils/adeBackupAutomatico";
import { MODULOS_BACKUP, restaurarBackupModulo } from "./utils/adeBackupPorModulo";
import { nwDisponivel } from "./utils/nwModulosJanela";
import { hidratarCalcPlanilha } from "./utils/calculadoraPlanilhaPersistencia";

type Credentials = {
  login: string;
  password: string;
};

const CREDENTIALS_STORAGE_KEY = "ade_auth_credentials";
const THEME_STORAGE_KEY = "ade_theme_preset";
const METAL_STORAGE_KEY = "ade_metal_preset";
const DARK_STORAGE_KEY = "ade_tema_escuro";
const PRINT_CLEAN_STORAGE_KEY = "ade_impressao_limpa";
const COPA_STORAGE_KEY = "ade_tema_copa";

type CopaEstiloId = "" | "brasil";
const COPA_ESTILOS: { id: CopaEstiloId; nome: string; descricao: string }[] = [
  { id: "", nome: "Desligado", descricao: "Botões e fundo normais" },
  {
    id: "brasil",
    nome: "Copa — Brasil",
    descricao: "Fundo com bandeiras + botões verde/amarelo",
  },
];
const safeSetLocalStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Evita quebra do app quando quota estiver cheia.
  }
};
const DEFAULT_CREDENTIALS: Credentials = {
  login: "ADE",
  password: "103911",
};

type ThemePresetId =
  | "dourado-verde"
  | "dourado-azul"
  | "dourado-rubi"
  | "dourado-roxo"
  | "esmeralda-luxo"
  | "safira-eletrico"
  | "ametista-mistico"
  | "ouro-rose-tema"
  | "diamante-prata"
  | "preto-grafite"
  | "neon-verde"
  | "neon-rosa"
  | "neon-azul"
  | "neon-laranja";

type ThemePreset = {
  id: ThemePresetId;
  nome: string;
  principal: string;
  sombra: string;
  brilho: string;
  accent: string;
  pos: string;
  neg: string;
  base: string;
  surface: string;
  surface2: string;
  shadowDark: string;
  /** Paleta neon: força fundo preto + efeitos de brilho (letreiro aceso). */
  neon?: boolean;
};

type MetalPresetId = "dourado-classico" | "diamante" | "rubi" | "ouro-rose";
type MetalPreset = {
  id: MetalPresetId;
  nome: string;
  gradiente: string;
  borda: string;
  destaque: string;
  brilho: string;
  sombraExterna: string;
  sombraInternaTopo: string;
  sombraInternaBase: string;
};

const THEME_PRESETS: ThemePreset[] = [
  {
    id: "dourado-verde",
    nome: "Dourado + Verde",
    principal: "#009b3a",
    sombra: "#046307",
    brilho: "#8de0a8",
    accent: "#009b3a",
    pos: "#009b3a",
    neg: "#c1121f",
    base: "#e8e8ed",
    surface: "#ebebf0",
    surface2: "#f0f0f5",
    shadowDark: "#c5c6ca",
  },
  {
    id: "dourado-azul",
    nome: "Dourado + Azul",
    principal: "#1d4ed8",
    sombra: "#0818A8",
    brilho: "#4FACFE",
    accent: "#1d4ed8",
    pos: "#1d4ed8",
    neg: "#c1121f",
    base: "#e8eaf0",
    surface: "#eceef5",
    surface2: "#f4f7ff",
    shadowDark: "#c2c8d4",
  },
  {
    id: "dourado-rubi",
    nome: "Dourado + Rubi",
    principal: "#b91c1c",
    sombra: "#8e1128",
    brilho: "#ff87aa",
    accent: "#b91c1c",
    pos: "#15803d",
    neg: "#b91c1c",
    base: "#efe8ea",
    surface: "#f4ecee",
    surface2: "#faf2f3",
    shadowDark: "#d4c3c8",
  },
  {
    id: "dourado-roxo",
    nome: "Dourado + Roxo",
    principal: "#7c3aed",
    sombra: "#4B0082",
    brilho: "#E0B0FF",
    accent: "#7c3aed",
    pos: "#16a34a",
    neg: "#dc2626",
    base: "#ece8f3",
    surface: "#f1edf7",
    surface2: "#f7f4fb",
    shadowDark: "#cbc3d8",
  },
  {
    id: "esmeralda-luxo",
    nome: "Esmeralda (Verde Luxuoso)",
    principal: "#50C878",
    sombra: "#046307",
    brilho: "#A7F432",
    accent: "#50C878",
    pos: "#50C878",
    neg: "#c1121f",
    base: "#e8efe8",
    surface: "#edf5ed",
    surface2: "#f5fbf5",
    shadowDark: "#bccfbc",
  },
  {
    id: "safira-eletrico",
    nome: "Safira (Azul Royal Elétrico)",
    principal: "#0F52BA",
    sombra: "#0818A8",
    brilho: "#4FACFE",
    accent: "#0F52BA",
    pos: "#0F52BA",
    neg: "#c1121f",
    base: "#e8edf6",
    surface: "#edf2fb",
    surface2: "#f5f9ff",
    shadowDark: "#bfc9dc",
  },
  {
    id: "ametista-mistico",
    nome: "Ametista (Roxo Místico)",
    principal: "#9966CC",
    sombra: "#4B0082",
    brilho: "#E0B0FF",
    accent: "#9966CC",
    pos: "#50C878",
    neg: "#c1121f",
    base: "#eee8f4",
    surface: "#f4eef9",
    surface2: "#faf6ff",
    shadowDark: "#cabed8",
  },
  {
    id: "ouro-rose-tema",
    nome: "Ouro Rosé (Metal Moderno)",
    principal: "#B76E79",
    sombra: "#8E4550",
    brilho: "#FFD1DC",
    accent: "#B76E79",
    pos: "#15803d",
    neg: "#B91C1C",
    base: "#f0e8ea",
    surface: "#f6eef0",
    surface2: "#fcf5f7",
    shadowDark: "#d3c0c6",
  },
  {
    id: "diamante-prata",
    nome: "Diamante / Prata (Brilho Gélido)",
    principal: "#E5E4E2",
    sombra: "#A9A9A9",
    brilho: "#FFFFFF",
    accent: "#A9A9A9",
    pos: "#0F52BA",
    neg: "#c1121f",
    base: "#eeeeef",
    surface: "#f4f4f5",
    surface2: "#fbfbfc",
    shadowDark: "#c8c8cc",
  },
  {
    id: "preto-grafite",
    nome: "Preto (Grafite)",
    principal: "#111827",
    sombra: "#000000",
    brilho: "#6b7280",
    accent: "#111827",
    pos: "#15803d",
    neg: "#b91c1c",
    base: "#e6e7ea",
    surface: "#ececf0",
    surface2: "#f4f4f6",
    shadowDark: "#c2c3c8",
  },
  {
    id: "neon-verde",
    nome: "Verde Neon",
    principal: "#39ff14",
    sombra: "#00c853",
    brilho: "#ccff90",
    accent: "#22c55e",
    pos: "#16a34a",
    neg: "#dc2626",
    base: "#e9f0e9",
    surface: "#eef5ee",
    surface2: "#f4faf4",
    shadowDark: "#bdcabd",
    neon: true,
  },
  {
    id: "neon-rosa",
    nome: "Rosa Neon",
    principal: "#ff10f0",
    sombra: "#c4007a",
    brilho: "#ff8ad8",
    accent: "#ec4899",
    pos: "#16a34a",
    neg: "#e11d48",
    base: "#f2e8f0",
    surface: "#f7eef5",
    surface2: "#fcf5fb",
    shadowDark: "#d4c0d0",
    neon: true,
  },
  {
    id: "neon-azul",
    nome: "Azul Neon",
    principal: "#00e5ff",
    sombra: "#00b8d4",
    brilho: "#84ffff",
    accent: "#06b6d4",
    pos: "#0284c7",
    neg: "#dc2626",
    base: "#e6eef2",
    surface: "#ecf4f7",
    surface2: "#f3fbfe",
    shadowDark: "#bcccd4",
    neon: true,
  },
  {
    id: "neon-laranja",
    nome: "Laranja Neon",
    principal: "#ff6d00",
    sombra: "#e65100",
    brilho: "#ffab40",
    accent: "#f97316",
    pos: "#16a34a",
    neg: "#dc2626",
    base: "#f1ebe6",
    surface: "#f6f0ec",
    surface2: "#fcf6f2",
    shadowDark: "#d4c8bc",
    neon: true,
  },
];

const METAL_PRESETS: MetalPreset[] = [
  {
    id: "dourado-classico",
    nome: "Dourado clássico",
    gradiente:
      "linear-gradient(145deg, #fff4c2 0%, #e8c547 18%, #d4af37 38%, #ffd700 52%, #c9a227 72%, #daa520 100%)",
    borda: "rgba(212, 175, 55, 0.65)",
    destaque: "#1a0f00",
    brilho:
      "radial-gradient(ellipse at center, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.35) 38%, transparent 72%)",
    sombraExterna: "0 4px 12px rgba(0, 0, 0, 0.18)",
    sombraInternaTopo: "inset 0 2px 0 rgba(255,255,255,0.55)",
    sombraInternaBase: "inset 0 -2px 0 rgba(90,60,0,0.25)",
  },
  {
    id: "diamante",
    nome: "Diamante brilhante",
    gradiente:
      "linear-gradient(145deg, #ffffff 0%, #f3f9ff 22%, #d9ecff 45%, #ffffff 62%, #c7e4ff 82%, #eef7ff 100%)",
    borda: "rgba(163, 200, 236, 0.85)",
    destaque: "#0b1a2f",
    brilho:
      "radial-gradient(ellipse at center, rgba(255,255,255,0.98) 0%, rgba(210,234,255,0.62) 40%, transparent 74%)",
    sombraExterna: "0 4px 14px rgba(87, 132, 171, 0.28)",
    sombraInternaTopo: "inset 0 2px 0 rgba(255,255,255,0.95)",
    sombraInternaBase: "inset 0 -2px 0 rgba(92,130,165,0.25)",
  },
  {
    id: "ouro-rose",
    nome: "Ouro Rosé",
    gradiente:
      "linear-gradient(145deg, #ffdbe2 0%, #f2a7b6 20%, #d68b9b 40%, #B76E79 60%, #8E4550 82%, #6d2f38 100%)",
    borda: "rgba(183, 110, 121, 0.75)",
    destaque: "#fff6f8",
    brilho:
      "radial-gradient(ellipse at center, rgba(255,230,236,0.95) 0%, rgba(255,186,203,0.5) 40%, transparent 74%)",
    sombraExterna: "0 4px 14px rgba(122, 67, 78, 0.33)",
    sombraInternaTopo: "inset 0 2px 0 rgba(255, 224, 230, 0.62)",
    sombraInternaBase: "inset 0 -2px 0 rgba(96, 40, 51, 0.3)",
  },
  {
    id: "rubi",
    nome: "Rubi premium",
    gradiente:
      "linear-gradient(145deg, #ffd9e2 0%, #ff7aa0 22%, #e11d48 46%, #ff4f87 62%, #be123c 82%, #8a0f2d 100%)",
    borda: "rgba(190, 18, 60, 0.78)",
    destaque: "#fff5f7",
    brilho:
      "radial-gradient(ellipse at center, rgba(255,225,235,0.94) 0%, rgba(255,138,176,0.48) 40%, transparent 74%)",
    sombraExterna: "0 4px 14px rgba(127, 10, 40, 0.35)",
    sombraInternaTopo: "inset 0 2px 0 rgba(255, 207, 222, 0.65)",
    sombraInternaBase: "inset 0 -2px 0 rgba(80, 8, 28, 0.28)",
  },
];

function normalizeLogin(value: string) {
  return value.trim().toLowerCase();
}

function nomeArquivoBackupAgora(): string {
  const agora = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `backup-ade-${agora.getFullYear()}-${pad(agora.getMonth() + 1)}-${pad(
    agora.getDate()
  )}_${pad(agora.getHours())}${pad(agora.getMinutes())}.json`;
}

function loadCredentials(): Credentials {
  try {
    const saved = localStorage.getItem(CREDENTIALS_STORAGE_KEY);
    if (!saved) return DEFAULT_CREDENTIALS;

    const parsed = JSON.parse(saved) as Partial<Credentials>;
    if (
      typeof parsed.login === "string" &&
      parsed.login.trim() &&
      typeof parsed.password === "string" &&
      parsed.password
    ) {
      return { login: parsed.login, password: parsed.password };
    }
  } catch {
    // Se o storage estiver inválido, usa o padrão.
  }
  return DEFAULT_CREDENTIALS;
}

/** false = bolão fora do ar no domínio. O programa no PC continua com o módulo. */
const BOLAO_PUBLICO_NO_AR = false;

function App() {
  const modoPublico = (() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const modo = params.get('modo');
    const protocolo = window.location.protocol;
    const appLocal =
      protocolo === 'file:' || protocolo === 'chrome-extension:';
    // Links públicos do bolão (compartilhados no WhatsApp) — interceptar mesmo fora do ar
    if (modo === 'palpitar' || modo === 'palpitar-mm' || modo === 'importar') return true;
    // Cardápio digital (produto separado — links com ?modo=cardapio...)
    if (modo === 'cardapio' || modo === 'cardapio-painel' || modo === 'cardapio-cozinha') return true;
    // Site na internet: sem ?modo= → página do domínio (não o programa ADE)
    if (!appLocal && !modo) return true;
    return false;
  })();

  if (modoPublico) {
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    const modo = params.get('modo');
    if (modo === 'cardapio' || modo === 'cardapio-painel' || modo === 'cardapio-cozinha') {
      return (
        <ErrorBoundary>
          <CardapioPublico modo={modo} />
        </ErrorBoundary>
      );
    }
    const ehRotaBolao =
      !modo || modo === 'palpitar' || modo === 'palpitar-mm' || modo === 'importar';
    if (BOLAO_PUBLICO_NO_AR && ehRotaBolao) {
      return (
        <ErrorBoundary>
          <PalpitarPublico />
        </ErrorBoundary>
      );
    }
    return (
      <ErrorBoundary>
        <PaginaPublica />
      </ErrorBoundary>
    );
  }
  return <AppPrivado />;
}

function AppPrivado() {
  const isJanelaFilha = Boolean(moduloFixoDaUrl());
  const authFilhaImediato = isJanelaFilha && authFilhaViaUrl();
  const [splashVisivel, setSplashVisivel] = useState(() => !authFilhaImediato);
  const [authFilhaResolvida, setAuthFilhaResolvida] = useState(
    () => !isJanelaFilha || authFilhaImediato
  );
  const [credentials, setCredentials] = useState<Credentials>(DEFAULT_CREDENTIALS);
  const [isAuthenticated, setIsAuthenticated] = useState(() => authFilhaImediato);
  const [loginInput, setLoginInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<"conta" | "tema" | "dados">("tema");
  const [newLogin, setNewLogin] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [settingsMessage, setSettingsMessage] = useState("");
  const [themeId, setThemeId] = useState<ThemePresetId>("dourado-verde");
  const [metalId, setMetalId] = useState<MetalPresetId>("dourado-classico");
  const [temaEscuro, setTemaEscuro] = useState(false);
  const [impressaoLimpa, setImpressaoLimpa] = useState(false);
  const [copaEstilo, setCopaEstilo] = useState<CopaEstiloId>("");
  const [backupMessage, setBackupMessage] = useState("");
  const [metaBackupAuto, setMetaBackupAuto] = useState<MetaBackupAutomatico>(() =>
    lerMetaBackupAutomatico()
  );
  const [backupAutoAtivo, setBackupAutoAtivo] = useState(() => backupAutomaticoEstaAtivo());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileAnaliseRef = useRef<HTMLInputElement | null>(null);

  const credentialsPreview = useMemo(
    () => `${credentials.login} / ${"*".repeat(Math.max(credentials.password.length, 4))}`,
    [credentials]
  );

  useEffect(() => {
    const splash = document.getElementById("splash-screen");
    if (splash) splash.remove();

    if (isJanelaFilha && authFilhaViaUrl()) return;
    if (isJanelaFilha) return;
    const t = setTimeout(() => setSplashVisivel(false), 2000);
    return () => clearTimeout(t);
  }, [isJanelaFilha]);

  /** Fallback: janela filha sem token na URL aguarda um instante e tenta de novo. */
  useEffect(() => {
    if (!isJanelaFilha || authFilhaImediato) return;

    let tentativas = 0;
    const id = setInterval(() => {
      tentativas += 1;
      if (authFilhaViaUrl()) {
        setIsAuthenticated(true);
        setSplashVisivel(false);
        setAuthFilhaResolvida(true);
        clearInterval(id);
      } else if (tentativas >= 20) {
        setSplashVisivel(false);
        setAuthFilhaResolvida(true);
        clearInterval(id);
      }
    }, 250);

    return () => clearInterval(id);
  }, [isJanelaFilha, authFilhaImediato]);

  /** Heartbeat na janela principal — janelas filhas reutilizam a sessão. */
  useEffect(() => {
    if (!isAuthenticated || moduloFixoDaUrl()) return;
    return iniciarHeartbeatSessaoDesktop();
  }, [isAuthenticated]);

  /** Planilha grande só no IndexedDB — libera quota do LS (senão janela nova pede senha). */
  useEffect(() => {
    if (!isAuthenticated) return;
    void hidratarCalcPlanilha();
  }, [isAuthenticated]);

  /** Backup automático a cada hora — só janela principal (NW.js). */
  useEffect(() => {
    if (!isAuthenticated || isJanelaFilha) return;
    const versao = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "";
    return iniciarServicoBackupAutomatico(versao);
  }, [isAuthenticated, isJanelaFilha]);

  /** Restaura backup marcado (FORCAR-RESTAURAR em Backups/completo) uma única vez. */
  useEffect(() => {
    if (!isAuthenticated || isJanelaFilha) return;
    let cancelado = false;
    void (async () => {
      const r = await tentarRestaurarBackupMarcadoUmaVez();
      if (cancelado || !r.ok) return;
      setBackupMessage(r.mensagem);
      window.alert(r.mensagem + '\n\nO app vai recarregar para aplicar tema e dados.');
      window.location.reload();
    })();
    return () => {
      cancelado = true;
    };
  }, [isAuthenticated, isJanelaFilha]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(() => setMetaBackupAuto(lerMetaBackupAutomatico()), 60_000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  /** Logout na principal encerra sessão nas janelas filhas (evento storage). */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SESSAO_DESKTOP_STORAGE_KEY || e.newValue) return;
      setIsAuthenticated(false);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(METAL_STORAGE_KEY) as MetalPresetId | null;
    if (saved && METAL_PRESETS.some((p) => p.id === saved)) setMetalId(saved);
  }, []);

  useEffect(() => {
    setCredentials(loadCredentials());
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY) as ThemePresetId | null;
    if (saved && THEME_PRESETS.some((p) => p.id === saved)) setThemeId(saved);
  }, []);

  useEffect(() => {
    setTemaEscuro(localStorage.getItem(DARK_STORAGE_KEY) === "1");
    setImpressaoLimpa(localStorage.getItem(PRINT_CLEAN_STORAGE_KEY) === "1");
    const copaSalvo = localStorage.getItem(COPA_STORAGE_KEY) as CopaEstiloId | null;
    if (copaSalvo && COPA_ESTILOS.some((c) => c.id === copaSalvo)) setCopaEstilo(copaSalvo);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (copaEstilo) root.setAttribute("data-copa", copaEstilo);
    else root.removeAttribute("data-copa");
    safeSetLocalStorage(COPA_STORAGE_KEY, copaEstilo);
  }, [copaEstilo]);

  useEffect(() => {
    safeSetLocalStorage(DARK_STORAGE_KEY, temaEscuro ? "1" : "0");
  }, [temaEscuro]);

  useEffect(() => {
    const root = document.documentElement;
    if (impressaoLimpa) root.setAttribute("data-impressao-limpa", "1");
    else root.removeAttribute("data-impressao-limpa");
    safeSetLocalStorage(PRINT_CLEAN_STORAGE_KEY, impressaoLimpa ? "1" : "0");
  }, [impressaoLimpa]);

  useEffect(() => {
    const preset = THEME_PRESETS.find((p) => p.id === themeId) || THEME_PRESETS[0];
    const root = document.documentElement;
    const isNeon = Boolean(preset.neon);
    // Paleta neon sempre força fundo escuro (alto contraste para o brilho).
    const escuro = temaEscuro || isNeon;

    // Atributos que ligam os overrides de CSS (escuro/neon).
    if (escuro) root.setAttribute("data-tema-escuro", "1");
    else root.removeAttribute("data-tema-escuro");
    if (isNeon) root.setAttribute("data-neon", "1");
    else root.removeAttribute("data-neon");

    // No neon usamos a cor "principal" (vibrante) como destaque e brilho.
    const accent = isNeon ? preset.principal : preset.accent;
    root.style.setProperty("--ade-accent", accent);
    root.style.setProperty("--ade-saldo-pos", preset.pos);
    root.style.setProperty("--ade-saldo-neg", preset.neg);
    root.style.setProperty("--ade-neon", isNeon ? preset.principal : "transparent");

    if (isNeon) {
      // Fundo quase preto, levemente tingido com a cor neon. Texto bem claro.
      const a = preset.principal;
      root.style.setProperty("--ade-neu-base", `color-mix(in srgb, ${a} 6%, #050608)`);
      root.style.setProperty("--ade-neu-surface", `color-mix(in srgb, ${a} 8%, #0a0b10)`);
      root.style.setProperty("--ade-neu-surface-2", `color-mix(in srgb, ${a} 11%, #0e1018)`);
      root.style.setProperty("--ade-shadow-dark", "#000000");
      root.style.setProperty("--ade-shadow-light", `color-mix(in srgb, ${a} 14%, #16192a)`);
      root.style.setProperty("--ade-text", "#f2f5ff");
    } else if (temaEscuro) {
      // Cada paleta tinge o fundo escuro com a própria cor (verde → fundo
      // esverdeado, azul → azulado, etc.) usando color-mix sobre tons quase
      // pretos. Mantém o texto claro fixo.
      const a = preset.accent;
      root.style.setProperty("--ade-neu-base", `color-mix(in srgb, ${a} 9%, #15161a)`);
      root.style.setProperty("--ade-neu-surface", `color-mix(in srgb, ${a} 12%, #1d1e23)`);
      root.style.setProperty("--ade-neu-surface-2", `color-mix(in srgb, ${a} 15%, #232429)`);
      root.style.setProperty("--ade-shadow-dark", `color-mix(in srgb, ${a} 7%, #0c0d10)`);
      root.style.setProperty("--ade-shadow-light", `color-mix(in srgb, ${a} 18%, #303138)`);
      root.style.setProperty("--ade-text", "#e8e8ed");
    } else {
      root.style.setProperty("--ade-neu-base", preset.base);
      root.style.setProperty("--ade-neu-surface", preset.surface);
      root.style.setProperty("--ade-neu-surface-2", preset.surface2);
      root.style.setProperty("--ade-shadow-dark", preset.shadowDark);
      root.style.setProperty("--ade-shadow-light", "#ffffff");
      root.style.setProperty("--ade-text", "#000000");
    }
    safeSetLocalStorage(THEME_STORAGE_KEY, preset.id);
  }, [themeId, temaEscuro]);

  useEffect(() => {
    const metal = METAL_PRESETS.find((p) => p.id === metalId) || METAL_PRESETS[0];
    const root = document.documentElement;
    root.style.setProperty("--ade-gold-gradient", metal.gradiente);
    root.style.setProperty("--ade-gold-border", metal.borda);
    root.style.setProperty("--ade-gold-text", metal.destaque);
    root.style.setProperty("--ade-gold-highlight", metal.brilho);
    root.style.setProperty("--ade-gold-shadow-outer", metal.sombraExterna);
    root.style.setProperty("--ade-gold-shadow-top", metal.sombraInternaTopo);
    root.style.setProperty("--ade-gold-shadow-bottom", metal.sombraInternaBase);
    safeSetLocalStorage(METAL_STORAGE_KEY, metal.id);
  }, [metalId]);

  const handleLogin = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const loginValido = normalizeLogin(loginInput) === normalizeLogin(credentials.login);
    const senhaValida = passwordInput === credentials.password;

    if (loginValido && senhaValida) {
      setIsAuthenticated(true);
      if (!moduloFixoDaUrl()) {
        registrarLoginDesktop();
      }
      setLoginInput("");
      setPasswordInput("");
      return;
    }
  };

  const handleSaveCredentials = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const loginLimpo = newLogin.trim();

    if (!loginLimpo || !newPassword) {
      setSettingsMessage("Preencha login e senha para salvar.");
      return;
    }

    const updated: Credentials = { login: loginLimpo, password: newPassword };
    safeSetLocalStorage(CREDENTIALS_STORAGE_KEY, JSON.stringify(updated));
    setCredentials(updated);
    setNewLogin("");
    setNewPassword("");
    setSettingsMessage("Credenciais atualizadas com sucesso.");
  };

  const handleExportarBackup = async () => {
    try {
      const versao = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "";
      const payload = await montarBackupCompleto(versao);
      baixarJsonBackup(payload, nomeArquivoBackupAgora());
      const chavesLs = Object.keys(payload.dados).length;
      const chavesIdb = Object.keys(payload.indexedDB).length;
      setBackupMessage(
        `Backup completo gerado (${chavesLs} chaves localStorage + ${chavesIdb} blocos IndexedDB: lotes, tempo de corte, histórico…). Guarde o .json em local seguro.`
      );
    } catch {
      setBackupMessage("Não foi possível gerar o backup. Tente novamente.");
    }
  };

  const handleDiagnosticarDados = async () => {
    const diag = await diagnosticarTodasFontesDados();
    const linhas = diag.fontes
      .filter((f) => f.lotes > 0 || f.produtosTempoCorte > 0)
      .map(
        (f) =>
          `• ${f.fonte}: ${f.lotes ? `${f.lotes} lotes, maior nº ${f.maiorLote}` : ""}${
            f.produtosTempoCorte ? `${f.produtosTempoCorte} produtos tempo corte` : ""
          }${f.jun11 ? `, ${f.jun11} com data 11/06` : ""}`
      )
      .join("\n");
    window.alert(
      `Diagnóstico de dados (${new Date(diag.data).toLocaleString("pt-BR")})\n\n${linhas || "(nenhuma fonte com lotes/tempo corte)"}\n\n${diag.textoResumo}`
    );
    setBackupMessage(diag.textoResumo);
  };

  const handleAnalisarBackupArquivo = () => {
    fileAnaliseRef.current?.click();
  };

  const handleArquivoAnaliseBackup = (e: FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const lotes = extrairLotesDeBackupJson(String(reader.result ?? ""));
      const nums = lotes.map((l) => parseInt(String(l.lote).replace(/\D/g, ""), 10)).filter((n) => n > 0);
      const max = nums.length ? Math.max(...nums) : 0;
      const jun11 = lotes.filter(
        (x) =>
          String(x.dataHoraInicioCorte || "").includes("2026-06-11") ||
          String(x.dataHoraFimEmbalagem || "").includes("2026-06-11")
      ).length;
      const acima = nums.filter((n) => n > 8729).length;
      window.alert(
        `Arquivo: ${file.name}\n\nLotes no arquivo: ${lotes.length}\nMaior nº de lote: ${max || "—"}\nLotes > 8729: ${acima}\nCom data 11/06/2026: ${jun11}\n\n${
          max > 8729
            ? "Este arquivo TEM dados que faltam no app. Use Restaurar backup para importar."
            : max > 0
              ? "Este arquivo não tem lotes acima de 8729."
              : "Nenhum lote encontrado neste arquivo."
        }`
      );
    };
    reader.readAsText(file);
    input.value = "";
  };

  const handleArquivoBackup = (e: FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const texto = String(reader.result ?? "");
      let tipo: "modulo" | "completo" = "completo";
      try {
        const p = JSON.parse(texto) as { _tipo?: string };
        if (p._tipo === "backup-ade-modulo") tipo = "modulo";
      } catch {
        input.value = "";
        setBackupMessage("Arquivo inválido.");
        return;
      }

      const confirmar =
        tipo === "modulo"
          ? window.confirm(
              "Restaurar backup de UM MÓDULO (ex.: só Catálogo ou só Lotes)?\n\n" +
                "Isso atualiza só os dados daquela aba — o resto do app não é apagado.\n\nContinuar?"
            )
          : window.confirm(
              "Restaurar backup COMPLETO vai SUBSTITUIR os dados atuais do app pelos dados do arquivo. Deseja continuar?"
            );
      if (!confirmar) {
        input.value = "";
        return;
      }

      if (tipo === "modulo") {
        const r = await restaurarBackupModulo(texto);
        if (r.ok) {
          setBackupMessage(
            `Backup do módulo "${r.label || r.modulo}" restaurado (${r.chaves} LS + ${r.chavesIdb} IDB). Recarregando…`
          );
          setTimeout(() => window.location.reload(), 1200);
        } else {
          setBackupMessage(r.erro || "Falha ao restaurar módulo.");
        }
      } else {
        const r = await restaurarBackupCompleto(texto);
        if (r.ok) {
          setBackupMessage(
            `Backup restaurado (${r.chaves} chaves localStorage${r.chavesIdb ? ` + ${r.chavesIdb} blocos IndexedDB` : ""}). O app vai recarregar...`
          );
          setTimeout(() => window.location.reload(), 1200);
        } else {
          setBackupMessage(r.erro || "Falha ao restaurar o backup.");
        }
      }
    };
    reader.onerror = () => setBackupMessage("Erro ao ler o arquivo.");
    reader.readAsText(file);
    input.value = "";
  };

  const handleBackupAutomaticoAgora = async () => {
    const versao = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "";
    setBackupMessage("Gerando backup automático…");
    const r = await executarBackupAutomatico(versao);
    if (r.meta) setMetaBackupAuto(r.meta);
    if (r.ok) {
      const qtdMod = r.meta?.modulos ? Object.keys(r.meta.modulos).length : 0;
      setBackupMessage(
        `Backup salvo: completo + ${qtdMod} aba(s) em ${r.meta?.pastaRaiz ?? "Backups"}.`
      );
    } else {
      setBackupMessage(r.erro || "Falha no backup automático.");
    }
  };

  const handleToggleBackupAutomatico = () => {
    const novo = !backupAutoAtivo;
    setBackupAutomaticoAtivo(novo);
    setBackupAutoAtivo(novo);
    if (novo && isAuthenticated && !isJanelaFilha) {
      const versao = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "";
      iniciarServicoBackupAutomatico(versao);
      setBackupMessage("Backup automático ativado (a cada 1 hora).");
    } else {
      pararServicoBackupAutomatico();
      setBackupMessage("Backup automático desativado.");
    }
  };

  const openSettings = () => {
    setShowSettings(true);
    setSettingsTab("tema");
    setSettingsMessage("");
    setBackupMessage("");
    setMetaBackupAuto(lerMetaBackupAutomatico());
  };

  const closeSettings = () => {
    setShowSettings(false);
    setSettingsMessage("");
    setNewLogin("");
    setNewPassword("");
  };

  return (
    <>
      {splashVisivel && (
        <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-[#e8e8ed] font-sans text-black">
          <img src={splashImg} alt="ADE Projetos" className="max-w-[90%] max-h-[70vh] object-contain [filter:drop-shadow(10px_10px_20px_#c5c6ca)]" />
          <div className="mt-8 h-11 w-11 rounded-full border-2 border-[#009b3a]/35 border-t-[#009b3a] shadow-neu animate-spin" />
        </div>
      )}

      {!splashVisivel && !isAuthenticated && authFilhaResolvida && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#e8e8ed] p-4 font-sans text-black">
          <form
            onSubmit={handleLogin}
            className="ade-neu-card w-full max-w-md p-8"
          >
            <h1 className="mb-6 text-center text-lg font-bold tracking-tight text-black">PLANO DE CORTE ADE</h1>
            <input
              type="text"
              value={loginInput}
              onChange={(e) => setLoginInput(e.target.value)}
              className="ade-neu-input w-full px-4 py-3 text-black placeholder:text-neutral-500"
              placeholder="Digite seu login"
              autoFocus
            />

            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="ade-neu-input mt-4 w-full px-4 py-3 text-black placeholder:text-neutral-500"
              placeholder="Digite sua senha"
            />

            <button
              type="submit"
              className="ade-btn-gold relative z-10 mt-6 w-full px-4 py-3"
            >
              Entrar
            </button>
          </form>
        </div>
      )}

      {isAuthenticated && (
        <>
          <div className="fixed bottom-3 right-3 z-[9000] flex gap-2">
            <button
              type="button"
              onClick={openSettings}
              className="ade-neu-card-sm border border-[var(--ade-accent)]/35 p-2.5 text-black hover:brightness-[1.02]"
              title="Configurações"
            >
              <Settings className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                encerrarSessaoDesktop();
                setIsAuthenticated(false);
              }}
              className="ade-neu-card-sm border border-[#009b3a]/30 px-3 py-2 text-xs font-semibold text-black hover:brightness-[1.02]"
            >
              Sair
            </button>
          </div>

          {showSettings && (
            <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]">
              <form
                onSubmit={handleSaveCredentials}
                className="ade-neu-card w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto"
              >
                <h2 className="text-xl font-bold text-black">Configurações</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSettingsTab("tema")}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold ${settingsTab === "tema" ? "ade-btn-gold relative z-10" : "ade-neu-card-sm"}`}
                  >
                    <Palette className="mr-1 inline h-3.5 w-3.5" />
                    Aparência
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettingsTab("dados")}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold ${settingsTab === "dados" ? "ade-btn-gold relative z-10" : "ade-neu-card-sm"}`}
                  >
                    <Database className="mr-1 inline h-3.5 w-3.5" />
                    Backup
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettingsTab("conta")}
                    className={`rounded-lg px-3 py-2 text-xs font-semibold ${settingsTab === "conta" ? "ade-btn-gold relative z-10" : "ade-neu-card-sm"}`}
                  >
                    <Settings className="mr-1 inline h-3.5 w-3.5" />
                    Login e senha
                  </button>
                </div>

                {settingsTab === "tema" && (
                  <div className="mt-4">
                    <p className="mb-2 text-sm font-medium text-black">Paleta de cores</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {THEME_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => {
                            setThemeId(preset.id);
                            setSettingsMessage(`Tema aplicado: ${preset.nome}`);
                          }}
                          className={`rounded-xl border p-3 text-left ${themeId === preset.id ? "border-[var(--ade-accent)] ring-2 ring-[var(--ade-accent)]/35" : "border-black/10"}`}
                        >
                          <p className="text-sm font-semibold text-black">{preset.nome}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="h-5 w-5 rounded-full border border-black/15" style={{ background: preset.principal }} title="Cor principal" />
                            <span className="h-5 w-5 rounded-full border border-black/15" style={{ background: preset.sombra }} title="Sombra" />
                            <span className="h-5 w-5 rounded-full border border-black/15" style={{ background: preset.brilho }} title="Brilho" />
                            <span className="h-5 w-5 rounded-full border border-black/15" style={{ background: preset.accent }} title="Accent" />
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="mb-2 mt-5 text-sm font-medium text-black">Acabamento metálico dos botões</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                      {METAL_PRESETS.map((metal) => (
                        <button
                          key={metal.id}
                          type="button"
                          onClick={() => {
                            setMetalId(metal.id);
                            setSettingsMessage(`Metal aplicado: ${metal.nome}`);
                          }}
                          className={`rounded-xl border p-3 text-left ${metalId === metal.id ? "border-[var(--ade-accent)] ring-2 ring-[var(--ade-accent)]/35" : "border-black/10"}`}
                        >
                          <p className="text-sm font-semibold text-black">{metal.nome}</p>
                          <div
                            className="mt-2 h-8 rounded-lg border border-black/10"
                            style={{ background: metal.gradiente }}
                          />
                        </button>
                      ))}
                    </div>
                    <p className="mb-2 mt-5 text-sm font-medium text-black">Modo de exibição</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => {
                          setTemaEscuro((v) => !v);
                          setSettingsMessage(
                            !temaEscuro ? "Tema escuro ativado." : "Tema claro ativado."
                          );
                        }}
                        className={`flex items-center justify-between rounded-xl border p-3 text-left ${temaEscuro ? "border-[var(--ade-accent)] ring-2 ring-[var(--ade-accent)]/35" : "border-black/10"}`}
                      >
                        <span className="flex items-center gap-2">
                          {temaEscuro ? (
                            <Moon className="h-4 w-4 text-[var(--ade-accent)]" />
                          ) : (
                            <Sun className="h-4 w-4 text-[var(--ade-accent)]" />
                          )}
                          <span className="text-sm font-semibold text-black">Tema escuro</span>
                        </span>
                        <span className={`text-xs font-bold ${temaEscuro ? "text-[#009b3a]" : "text-neutral-500"}`}>
                          {temaEscuro ? "LIGADO" : "desligado"}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setImpressaoLimpa((v) => !v);
                          setSettingsMessage(
                            !impressaoLimpa
                              ? "Impressão econômica ativada (preto e branco)."
                              : "Impressão econômica desativada."
                          );
                        }}
                        className={`flex items-center justify-between rounded-xl border p-3 text-left ${impressaoLimpa ? "border-[var(--ade-accent)] ring-2 ring-[var(--ade-accent)]/35" : "border-black/10"}`}
                      >
                        <span className="flex items-center gap-2">
                          <Printer className="h-4 w-4 text-[var(--ade-accent)]" />
                          <span className="text-sm font-semibold text-black">Impressão limpa</span>
                        </span>
                        <span className={`text-xs font-bold ${impressaoLimpa ? "text-[#009b3a]" : "text-neutral-500"}`}>
                          {impressaoLimpa ? "LIGADO" : "desligado"}
                        </span>
                      </button>
                    </div>
                    <p className="mt-3 text-xs text-neutral-600">
                      <strong>Tema escuro:</strong> deixa o app em tons escuros (mais
                      confortável à noite). <strong>Impressão limpa:</strong> ao imprimir
                      (Ctrl+P), sai em preto e branco, sem fundos nem sombras — economiza
                      tinta.
                    </p>

                    <div className="mt-5 border-t border-black/10 pt-4">
                      <p className="flex items-center gap-2 text-sm font-medium text-black">
                        <Flag className="h-4 w-4 text-[var(--ade-accent)]" />
                        Tema Copa do Mundo
                      </p>
                      <p className="mt-1 text-xs text-neutral-600">
                        Deixa o fundo com bandeiras do Brasil (transparentes) e os botões
                        nas cores verde e amarelo. Os ícones e textos continuam iguais, então
                        você sabe a função de cada botão.
                      </p>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {COPA_ESTILOS.map((c) => (
                          <button
                            key={c.id || "off"}
                            type="button"
                            onClick={() => {
                              setCopaEstilo(c.id);
                              setSettingsMessage(
                                c.id
                                  ? "Tema Copa do Mundo ativado."
                                  : "Tema Copa do Mundo desativado."
                              );
                            }}
                            className={`flex flex-col items-start gap-0.5 rounded-xl border p-3 text-left ${
                              copaEstilo === c.id
                                ? "border-[var(--ade-accent)] ring-2 ring-[var(--ade-accent)]/35"
                                : "border-black/10"
                            }`}
                          >
                            <span className="text-sm font-semibold text-black">{c.nome}</span>
                            <span className="text-xs text-neutral-500">{c.descricao}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {settingsTab === "dados" && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-black">Cópia de segurança (backup)</p>
                    <p className="mt-1 text-xs text-neutral-600">
                      Salva <strong>todos os dados</strong> do app — localStorage <strong>e</strong> IndexedDB
                      (lotes, tempo de corte, histórico, projetos, estoque, Copa…) num único arquivo
                      <strong> .json</strong>.
                    </p>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => void handleExportarBackup()}
                        className="ade-btn-gold relative z-10 flex items-center justify-center gap-2 px-4 py-3 font-semibold"
                      >
                        <Download className="h-4 w-4" />
                        Exportar backup
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="ade-neu-card-sm flex items-center justify-center gap-2 border border-[#009b3a]/35 px-4 py-3 font-semibold text-black"
                      >
                        <Upload className="h-4 w-4" />
                        Restaurar backup
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDiagnosticarDados()}
                        className="ade-neu-card-sm flex items-center justify-center gap-2 border border-amber-500/40 px-4 py-3 font-semibold text-black sm:col-span-2"
                      >
                        <Database className="h-4 w-4" />
                        Diagnosticar lotes e tempo de corte (neste PC)
                      </button>
                      <button
                        type="button"
                        onClick={handleAnalisarBackupArquivo}
                        className="ade-neu-card-sm flex items-center justify-center gap-2 border border-[#009b3a]/35 px-4 py-3 text-sm font-semibold text-black sm:col-span-2"
                      >
                        Analisar arquivo .json (sem restaurar)
                      </button>
                    </div>
                    <input
                      ref={fileAnaliseRef}
                      type="file"
                      accept="application/json,.json"
                      className="hidden"
                      onChange={handleArquivoAnaliseBackup}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/json,.json"
                      className="hidden"
                      onChange={handleArquivoBackup}
                    />

                    {nwDisponivel() && (
                      <div className="mt-5 rounded-xl border border-[#009b3a]/25 bg-[#e8f5ec]/40 p-4">
                        <p className="text-sm font-semibold text-black flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#009b3a]" />
                          Backup automático (a cada 1 hora)
                        </p>
                        <p className="mt-1 text-xs text-neutral-700">
                          Salva na pasta <strong>Backups</strong> ao lado do programa (preferencial)
                          ou em <strong>Documentos/Sistema Ade Backups</strong>: um arquivo{" "}
                          <strong>completo</strong> e <strong>uma pasta por aba</strong> (Catálogo,
                          Lotes, Tempo de Corte, Copa…) com <strong>ultimo.json</strong> + histórico
                          em <em>rotativo/</em> (últimas {MAX_ROTATIVOS_POR_MODULO} cópias por aba).
                          {" "}
                          <strong>Requer o app desktop aberto</strong> — verifica a cada 1 hora
                          (checagem a cada 5 min; recupera após suspensão do PC).
                        </p>
                        <p className="mt-2 text-xs text-neutral-800">
                          <strong>Pasta:</strong>{" "}
                          {metaBackupAuto.pastaRaiz || pastaBackupsRaiz() || "—"}
                        </p>
                        <p className="mt-1 text-xs text-neutral-800">
                          <strong>Completo:</strong>{" "}
                          {metaBackupAuto.caminho || caminhoBackupAutomatico() || "completo/backup-ade-automatico.json"}
                        </p>
                        <p className="mt-1 text-xs text-neutral-800">
                          <strong>Último backup:</strong> {formatarDataBackup(metaBackupAuto.ultimoBackup)}
                          {(() => {
                            const prox = estimarProximoBackupAutomatico();
                            if (!prox || !backupAutoAtivo) return null;
                            return (
                              <>
                                {" "}
                                · <strong>Próximo (~):</strong>{" "}
                                {formatarDataBackup(prox.toISOString())}
                              </>
                            );
                          })()}
                          {metaBackupAuto.chavesLocalStorage > 0 && (
                            <>
                              {" "}
                              · {metaBackupAuto.chavesLocalStorage} LS + {metaBackupAuto.chavesIndexedDB}{" "}
                              IDB
                            </>
                          )}
                        </p>
                        {metaBackupAuto.modulos && Object.keys(metaBackupAuto.modulos).length > 0 && (
                          <div className="mt-2 text-[11px] text-neutral-700 space-y-0.5 max-h-36 overflow-y-auto">
                            <p className="font-semibold text-neutral-800">Módulos salvos:</p>
                            {Object.entries(metaBackupAuto.modulos).map(([id, m]) => (
                              <p key={id}>
                                • <strong>{m.label}</strong> — {m.rotativos} cópia(s) em rotativo/
                                {m.resumo?.projetosCatalogo != null && ` · ${m.resumo.projetosCatalogo} projetos`}
                                {m.resumo?.lotes != null && ` · ${m.resumo.lotes} lotes`}
                                {m.resumo?.participantesCopa != null &&
                                  ` · ${m.resumo.participantesCopa} participantes Copa`}
                              </p>
                            ))}
                          </div>
                        )}
                        <details className="mt-2 text-[11px] text-neutral-600">
                          <summary className="cursor-pointer font-medium text-neutral-800">
                            Pastas por aba (para restaurar só uma área)
                          </summary>
                          <ul className="mt-1 ml-3 list-disc space-y-0.5">
                            {MODULOS_BACKUP.map((m) => (
                              <li key={m.id}>
                                {m.label}:{" "}
                                <code className="text-[10px]">
                                  {pastaModuloBackup(m.id)?.replace(/\\/g, "/") ?? m.id}/ultimo.json
                                </code>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2">
                            Restaurar: Configurações → Restaurar backup → escolha{" "}
                            <em>ultimo.json</em> ou um arquivo datado em <em>rotativo/</em> da pasta
                            da aba. Só aquela área é atualizada.
                          </p>
                        </details>
                        {metaBackupAuto.ultimoErro && (
                          <p className="mt-1 text-xs text-rose-700">Erro: {metaBackupAuto.ultimoErro}</p>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2 items-center">
                          <label className="flex items-center gap-2 text-sm text-black cursor-pointer">
                            <input
                              type="checkbox"
                              checked={backupAutoAtivo}
                              onChange={handleToggleBackupAutomatico}
                              className="rounded border-[#009b3a]/40"
                            />
                            Ativo ({INTERVALO_BACKUP_AUTOMATICO_MS / 3600000}h)
                          </label>
                          <button
                            type="button"
                            onClick={() => void handleBackupAutomaticoAgora()}
                            className="px-3 py-1.5 rounded-lg border border-[#009b3a]/40 bg-white text-sm font-medium text-[#006b28] hover:bg-[#e8f5ec]"
                          >
                            Fazer backup agora
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 rounded-xl border border-[#009b3a]/20 bg-[var(--ade-neu-surface-2)] p-3 text-xs text-neutral-700">
                      <p className="font-semibold text-black">Como usar:</p>
                      <ol className="mt-1 list-decimal space-y-1 pl-4">
                        <li>
                          <strong>Para salvar:</strong> clique em "Exportar backup". Um
                          arquivo <em>backup-ade-DATA.json</em> será baixado. Guarde-o em um
                          pen drive, no Google Drive ou no seu e-mail.
                        </li>
                        <li>
                          <strong>Para recuperar:</strong> clique em "Restaurar backup",
                          escolha o arquivo .json salvo e confirme. O app substitui os dados
                          atuais e recarrega sozinho.
                        </li>
                        <li>
                          No app desktop (aberto), o <strong>backup automático</strong> roda a cada
                          1 hora na pasta indicada acima (geralmente{" "}
                          <em>Backups/</em> ao lado do programa).
                        </li>
                      </ol>
                    </div>

                    {backupMessage && (
                      <p className="mt-3 text-sm font-medium text-[#009b3a]">{backupMessage}</p>
                    )}
                  </div>
                )}

                {settingsTab === "conta" && (
                  <>
                    <p className="mt-2 text-xs text-neutral-600">Atual: {credentialsPreview}</p>
                    <label className="mt-4 block text-sm font-medium text-black">Novo login</label>
                    <input
                      type="text"
                      value={newLogin}
                      onChange={(e) => setNewLogin(e.target.value)}
                      className="ade-neu-input mt-1 w-full px-3 py-2 text-black placeholder:text-neutral-500"
                      placeholder="Ex.: ADE"
                    />

                    <label className="mt-4 block text-sm font-medium text-black">Nova senha</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="ade-neu-input mt-1 w-full px-3 py-2 text-black placeholder:text-neutral-500"
                      placeholder="Digite a nova senha"
                    />
                  </>
                )}

                {settingsMessage && <p className="mt-3 text-sm font-medium text-[#009b3a]">{settingsMessage}</p>}

                <div className="sticky bottom-0 -mx-8 -mb-8 mt-5 flex gap-3 border-t border-[var(--ade-accent)]/15 bg-[var(--ade-neu-surface)] px-8 py-4">
                  {settingsTab === "conta" ? (
                    <button
                      type="submit"
                      className="ade-btn-gold relative z-10 flex-1 px-4 py-2.5"
                    >
                      Salvar login/senha
                    </button>
                  ) : (
                    <div className="flex-1" />
                  )}
                  <button
                    type="button"
                    onClick={closeSettings}
                    className="ade-neu-card-sm flex-1 border border-[#009b3a]/35 px-4 py-2.5 font-semibold text-black"
                  >
                    Fechar
                  </button>
                </div>
              </form>
            </div>
          )}

          <ErrorBoundary>
            <CalculadoraCorte />
          </ErrorBoundary>
        </>
      )}
    </>
  );
}
export default App;
